// src/pages/ListingDetail.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Container,
    Typography,
    Paper,
    Grid,
    CircularProgress,
    Box,
    Snackbar,
    Divider,
    Tooltip
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { styled } from '@mui/system';
import {
    getUuidListing,
    getFullReviewsForListing,
    createTransaction,
    getUserBasicInfo,
    createConversation,
    sendMessage,
    getBlockedPeriods,
    addToWantedBoard
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { addDays } from 'date-fns';
import { MapContainer, TileLayer, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import SEOProvider from '../components/SEOProvider';
import BreadcrumbNavigation from '../components/BreadcrumbNavigation';

import ImageGallery from '../components/ImageGallery';
import FeatureChips from '../components/FeatureChips';
import LenderInfo from '../components/LenderInfo';
import ReviewsList from '../components/ReviewsList';
import BookingSummary from '../components/BookingSummary';
import SendRequestModal from '../components/SendRequestModal';
import CategoryBreadcrumbs from '../components/CategoryBreadcrumbs';
import { StyledButton } from '../components/StyledComponents';
import AddAlertIcon from '@mui/icons-material/AddAlert';

import { format } from 'date-fns';
import NotFoundPage from './NotFoundPage';

const StyledContainer = styled(Container)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    textAlign: 'center',
    margin: theme.spacing(6, 0, 4),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
}));

const MapBox = styled(Box)(({ theme }) => ({
    height: 300,
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
}));

function ListingDetail() {
    const { currentUser } = useAuth();
    const [listing, setListing] = useState(null);
    const [ownerInfo, setOwnerInfo] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notFound, setNotFound] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: '' });
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [blockedPeriods, setBlockedPeriods] = useState([]);
    const { uuid } = useParams();
    const navigate = useNavigate();

    const parseLocalDateString = (dateStr) => {
        if (!dateStr) return null;
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
    };
    const [searchParams] = useSearchParams();
    const [startDate, setStartDate] = useState(
        parseLocalDateString(searchParams.get('startDate'))
    );
    const [endDate, setEndDate] = useState(
        parseLocalDateString(searchParams.get('endDate'))
    );

    useEffect(() => {
        const fetchListingAndReviews = async () => {
            try {
                setLoading(true);
                const listingResponse = await getUuidListing(uuid);

                if (listingResponse?.data) {
                    const fetchedListing = listingResponse.data;
                    setListing(fetchedListing);

                    const listingId = fetchedListing.id;
                    const [reviewsResponse, blockedPeriodsResponse] = await Promise.all([
                        getFullReviewsForListing(listingId),
                        getBlockedPeriods(listingId)
                    ]);

                    setReviews(
                        reviewsResponse?.data.map((review) => ({
                            id: review.id,
                            rating: review.rating,
                            comment: review.comment,
                            reviewer_first_name: review.reviewer.first_name,
                            reviewer_last_name: review.reviewer.last_name,
                            reviewer_profile_picture: review.reviewer.profile_picture
                        })) || []
                    );

                    if (fetchedListing.owner_id) {
                        const ownerResponse = await getUserBasicInfo(fetchedListing.owner_id);
                        setOwnerInfo(ownerResponse?.data);
                    }

                    if (blockedPeriodsResponse?.data) {
                        setBlockedPeriods(
                            blockedPeriodsResponse.data.map((period) => ({
                                start: new Date(period.start_date),
                                end: new Date(period.end_date)
                            }))
                        );
                    }
                } else {
                    // If the response exists but has no data, treat that as "not found"
                    setNotFound(true);
                }
            } catch (err) {
                // If the API returns a 404, mark as notFound
                if (err.response && err.response.status === 404) {
                    setNotFound(true);
                } else {
                    console.error('Error fetching listing and reviews', err);
                    setError('An error occurred while loading the listing. Please try again later.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchListingAndReviews();
    }, [uuid]);

    useEffect(() => {
        if (startDate && endDate && startDate > endDate) {
            setEndDate(addDays(startDate, 1));
        }
    }, [startDate, endDate]);

    const handleImageClick = (index) => {
        setCurrentImageIndex(index);
    };

    const handleOpenModal = () => {
        if (!currentUser) {
            setSnackbar({ open: true, message: 'Please log in to send a request.' });
            return;
        }
        if (currentUser.id === listing.owner_id) {
            setSnackbar({ open: true, message: 'You cannot book your own listing.' });
            return;
        }
        const defaultMessage = `Hi, I would like to know if this is available from ${startDate.toDateString()} to ${endDate ? endDate.toDateString() : 'N/A'
            }. Thanks!`;
        setMessage(defaultMessage);
        setModalOpen(true);
    };

    const handleAddWanted = async () => {
        if (!currentUser) {
            setSnackbar({ open: true, message: 'Please log in to use the wanted board.' });
            return;
        }
        try {
            await addToWantedBoard(listing.category_id);
            setSnackbar({ open: true, message: 'Category added to your wanted board!' });
        } catch (error) {
            console.error('Error adding to wanted board:', error);
            setSnackbar({ open: true, message: 'Failed to update wanted board.' });
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleSendRequest = async () => {
        try {
            if (!currentUser || !currentUser.id) {
                setSnackbar({ open: true, message: 'Please log in to proceed.' });
                return;
            }
            const formattedStartDate = format(startDate, 'yyyy-MM-dd');
            const formattedEndDate = format(endDate, 'yyyy-MM-dd');
            const transactionData = {
                borrower_id: currentUser.id,
                lender_id: listing.owner_id,
                listing_id: listing.id,
                transaction_amount: listing.price,
                start_date: formattedStartDate,
                end_date: endDate ? formattedEndDate : formattedStartDate
            };
            const transactionResponse = await createTransaction(transactionData);
            if (!transactionResponse?.data?.id) {
                throw new Error('Transaction creation failed, no transaction ID received.');
            }
            const transactionId = transactionResponse.data.id;

            const conversationData = {
                borrower_id: currentUser.id,
                lender_id: listing.owner_id,
                listing_id: listing.id,
                transaction_id: transactionId
            };
            const conversationResponse = await createConversation(conversationData);
            if (!conversationResponse?.data?.id) {
                throw new Error('Conversation creation failed.');
            }
            const messageData = {
                conversation_id: conversationResponse.data.id,
                content: message,
                borrower_id: currentUser.id,
                lender_id: listing.owner_id
            };
            await sendMessage(messageData);

            setSnackbar({ open: true, message: 'Request sent successfully!' });
            handleCloseModal();
            navigate(`/transactions/${transactionId}`);
        } catch (err) {
            console.error('Error sending request', err);
            setSnackbar({ open: true, message: 'Failed to send request. Please try again.' });
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading) {
        return (
            <StyledContainer maxWidth="lg">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <CircularProgress />
                </Box>
            </StyledContainer>
        );
    }

    if (notFound) {
        return <NotFoundPage />;
    }

    if (error) {
        return (
            <StyledContainer maxWidth="lg">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <Typography color="error">{error}</Typography>
                </Box>
            </StyledContainer>
        );
    }

    if (!listing || !ownerInfo) {
        return (
            <StyledContainer maxWidth="lg">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <Typography>No listing information available.</Typography>
                </Box>
            </StyledContainer>
        );
    }

    const pageTitle = `${listing.title} for Rent in ${listing.location} | Borrowed`;
    const pageDescription = `Rent ${listing.title} in ${listing.location}. ${listing.description.substring(
        0,
        150
    )}...`;

    const firstLocation = Array.isArray(listing.locations) && listing.locations.length > 0
        ? listing.locations[0]
        : null;

    return (
        <SEOProvider
            title={pageTitle}
            description={pageDescription}
            type="product"
            image={listing.images?.[0]?.image_url}
        >
            <StyledContainer maxWidth="lg">
                <BreadcrumbNavigation
                    customCrumbs={[
                        { to: '/listings', label: 'Listings' },
                        { to: `/listings/${listing.id}`, label: listing.title, isLast: true }
                    ]}
                />

                {currentUser?.id === listing.owner_id && (
                    <Box
                        sx={{
                            backgroundColor: 'info.light',
                            color: 'info.contrastText',
                            p: 2,
                            borderRadius: 1,
                            mb: 3,
                            textAlign: 'center'
                        }}
                    >
                        <Typography variant="body1">
                            You're viewing your own listing. This is how it appears to others.
                        </Typography>
                    </Box>
                )}

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <StyledPaper>
                            <CategoryBreadcrumbs categoryId={listing.category_id} />

                            <ImageGallery
                                images={listing.images}
                                currentImageIndex={currentImageIndex}
                                onImageClick={handleImageClick}
                            />
                            <Box mt={3}>
                                <Typography variant="h4" gutterBottom>
                                    {listing.title}
                                </Typography>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <LocationOnIcon color="action" />
                                    <Typography variant="subtitle1" ml={1}>
                                        {ownerInfo.zip_code}
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <Typography variant="h6" ml={1}>
                                        ${listing.price}/day
                                    </Typography>
                                </Box>
                            </Box>
                            <Divider />
                            <SectionTitle variant="h5">About this item</SectionTitle>
                            <Typography variant="body1" paragraph>
                                {listing.description}
                            </Typography>
                            <FeatureChips
                                features={[
                                    { label: 'Top-rated item', icon: <StarIcon /> },
                                    { label: 'Instant book' },
                                    { label: 'Free cancellation' }
                                ]}
                            />
                            <Divider />
                            <SectionTitle variant="h5">Lender Information</SectionTitle>
                            <LenderInfo lender={ownerInfo} />
                            <Divider />
                            <SectionTitle variant="h5">Location</SectionTitle>

                            {firstLocation ? (
                                <MapBox>
                                    <MapContainer
                                        center={[firstLocation.latitude, firstLocation.longitude]}
                                        zoom={13}
                                        style={{ height: '100%', width: '100%' }}
                                    >
                                        <TileLayer
                                            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                                            attribution='OpenStreetMaps'
                                        />
                                        <Circle
                                            center={[firstLocation.latitude, firstLocation.longitude]}
                                            radius={1000}
                                            pathOptions={{ color: '#4CAF50' }}
                                        />
                                    </MapContainer>
                                </MapBox>
                            ) : (
                                <Box
                                    sx={{
                                        height: 300,
                                        marginBottom: 2,
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#f5f5f5'
                                    }}
                                >
                                    <Typography variant="body1" color="textSecondary">
                                        Location not available
                                    </Typography>
                                </Box>
                            )}

                            <Divider />
                            <SectionTitle variant="h5">Reviews</SectionTitle>
                            <ReviewsList reviews={reviews} />
                        </StyledPaper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper elevation={2} sx={{ padding: 2, position: 'sticky', top: 16 }}>
                            <BookingSummary
                                listingId={listing.id}
                                pricePerDay={listing.price}
                                startDate={startDate}
                                endDate={endDate}
                                setStartDate={setStartDate}
                                setEndDate={setEndDate}
                                blockedPeriods={blockedPeriods}
                            />
                            {currentUser?.id === listing.owner_id ? (
                                <>
                                    <StyledButton
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        size="large"
                                        disabled
                                        sx={{ mt: 2 }}
                                    >
                                        Request to Book
                                    </StyledButton>
                                    <Typography variant="caption" display="block" align="center" sx={{ mt: 1 }}>
                                        This is how others see your listing
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <StyledButton
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        size="large"
                                        onClick={handleOpenModal}
                                        sx={{ mt: 2 }}
                                    >
                                        Request to Book
                                    </StyledButton>
                                    <Typography variant="caption" display="block" align="center" sx={{ mt: 1 }}>
                                        You won't be charged yet
                                    </Typography>
                                </>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
                <Box mt={4} textAlign="center">
                    <Tooltip title="Add this category to your wanted board and be notified about similar listings">
                        <StyledButton
                            variant="outlined"
                            startIcon={<AddAlertIcon />}
                            onClick={handleAddWanted}
                        >
                            Don't miss when someone uploads a similar rental!
                        </StyledButton>
                    </Tooltip>
                </Box>
                <SendRequestModal
                    open={modalOpen}
                    handleClose={handleCloseModal}
                    ownerFirstName={ownerInfo.first_name}
                    message={message}
                    setMessage={setMessage}
                    handleSendRequest={handleSendRequest}
                />
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    message={snackbar.message}
                />
            </StyledContainer>
        </SEOProvider>
    );
}

export default ListingDetail;
